import { Theme } from '@/theme';
import { useLoader } from '@CS/hooks';
import { isIOS } from '@CS/utils/Device';
import { useEffect, useRef, useState } from 'react';
import { Tile, ContentGrid } from './Tiles.styles';
import type { MouseEvent, TouchEvent } from 'react';
import type { ISDK } from '@tbwax_dev/mcd-family-sdk';
import type { Langs } from '@CS/components/TranslationProvider/types';
import type { CampaignUnit, Translations } from '@/services/api/types';

export type Size = 'small' | 'medium' | 'large';

type TilesProps = { sdk?: ISDK; lang?: Langs };

export const Tiles = ({ sdk, lang = 'it' }: TilesProps) => {
  const [loading, setLoading] = useState(true);
  const touchStart = useRef<{ x: number; y: number } | null>(null);

  const loader = useLoader(loading, { color: Theme.color.red });
  const [tiles, setTiles] = useState<CampaignUnit[] | null>(null);

  const getTranslation = (key: string, translations?: Translations) =>
    translations?.[key]?.[lang];

  const getLocalizedUrl = (translations?: Translations, fallback = '') =>
    getTranslation('cta_url', translations) ||
    translations?.cta_url?.de ||
    translations?.cta_url?.fr ||
    translations?.cta_url?.it ||
    fallback;

  const openUrl = (href: string) => {
    window.location.assign(href);
  };

  const handleClick = (
    href: string,
    event: MouseEvent<HTMLAnchorElement>
  ) => {
    if (!href) return event.preventDefault();
    if (isIOS) return;

    event.preventDefault();
    openUrl(href);
  };

  const handleTouchEnd = (
    href: string,
    event: TouchEvent<HTMLAnchorElement>
  ) => {
    if (!isIOS || !href) return;

    const touch = event.changedTouches[0];
    const start = touchStart.current;
    touchStart.current = null;

    if (!touch || !start) return;
    if (Math.abs(touch.clientX - start.x) > 10) return;
    if (Math.abs(touch.clientY - start.y) > 10) return;

    event.preventDefault();
    openUrl(href);
  };

  const handleTouchStart = (event: TouchEvent<HTMLAnchorElement>) => {
    if (!isIOS) return;

    const touch = event.touches[0];
    touchStart.current = touch && { x: touch.clientX, y: touch.clientY };
  };

  const getBackground = (color = '', image = '') =>
    `${color} ${image && `url(${image}) center/cover no-repeat`}`;

  const getCampaignUnits = (sdk: ISDK, useCached?: boolean) =>
    sdk.getCampaignUnits(useCached).then(setTiles);

  useEffect(() => {
    sdk &&
      !tiles &&
      getCampaignUnits(sdk).finally(
        () => ~setLoading(false) && getCampaignUnits(sdk, false)
      );
  }, [sdk]);

  return (
    loader || (
      <ContentGrid>
        {tiles &&
          tiles.map((tile, index) => {
            const ctaUrl = getLocalizedUrl(tile.translations, tile.ctaUrl);

            return (
              <Tile
                style={{
                  background: getBackground(tile.bgColor, tile.bgImageUrls?.[0])
                }}
                href={ctaUrl || undefined}
                onClick={(event) => handleClick(ctaUrl, event)}
                onTouchStart={handleTouchStart}
                onTouchEnd={(event) => handleTouchEnd(ctaUrl, event)}
                $size={tile.itemType as Size}
                data-size={tile.itemType}
                key={index}
              >
                {tile.itemType === 'large' ? (
                  <>
                    <img
                      src={tile.imageUrls[0]}
                      alt={tile.title}
                      width="177"
                      height="116"
                    />

                    <div>
                      <h3>{getTranslation('title', tile.translations)}</h3>
                      <p>{getTranslation('description', tile.translations)}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <img
                      src={tile.imageUrls[0]}
                      alt={tile.title}
                      width="70"
                      height="70"
                    />
                    <h3>{getTranslation('title', tile.translations)}</h3>
                    <p>{getTranslation('description', tile.translations)}</p>
                  </>
                )}
              </Tile>
            );
          })}
      </ContentGrid>
    )
  );
};
